import { useRef } from "react"

import { Button, Typography } from "@mui/material"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { BaseModal } from "@/components"
import FileDropZone from "@/components/FileDropZone"
import { useWebAdminTranslations } from "@/hooks"
import { useFileUpload } from "@/queries/hooks/useFileUpload"

import { PassHolderFormData } from "../../schema"

const PersonCertificateField = () => {
  const { t } = useTranslation()
  const { t: tWebAdmin } = useWebAdminTranslations()
  const MAX_SIZE = 3
  const pendingFileRef = useRef<File | null>(null)

  const { control, clearErrors, setValue } =
    useFormContext<PassHolderFormData>()

  const personCertificate = useWatch({ control, name: "personCertificate" })

  const { uploadFile, isUploading, error, resetError } = useFileUpload()

  const handleFileSelect = (file: File) => {
    pendingFileRef.current = file
    uploadFile(
      {
        file,
        localId: crypto.randomUUID(),
        fileType: "PersonCertificate",
      },
      {
        onSuccess: (fileId) => {
          clearErrors("personCertificate")
          setValue("personCertificate", {
            id: fileId.fileId,
            fileName: file.name,
          })
          pendingFileRef.current = null
        },
      }
    )
  }

  const handleRetry = () => {
    if (pendingFileRef.current) {
      handleFileSelect(pendingFileRef.current)
    }
  }

  return (
    <>
      <BaseModal
        open={!!error}
        onClose={resetError}
        title={t("passHolderDetails.form.errors.fileUploadFailedTitle")}
        footerContent={
          <Button variant="contained" onClick={() => handleRetry()}>
            {t("passHolderDetails.form.errors.fileUploadFailedRetry")}
          </Button>
        }
        maxWidth="sm"
      >
        <Typography variant="body2">
          {t("passHolderDetails.form.errors.fileUploadFailed")}
        </Typography>
      </BaseModal>

      <div data-field-name="personCertificate" style={{ scrollMarginTop: 120 }}>
        <Controller
          name="personCertificate"
          control={control}
          render={({ fieldState: { error } }) => (
            <FileDropZone
              textContent={{
                title: t("passHolderDetails.form.uploadPersonCertificate"),
                acceptedFormatsText: t(
                  "passHolderDetails.form.acceptedFormatsCertificateText",
                  { maxSize: MAX_SIZE }
                ),
                learnMoreTitle: tWebAdmin(
                  "[SKIPASS_PERSON_CERTIFICATE_READ_MORE_HEADING]"
                ),
                learnMoreDescription: tWebAdmin(
                  "[SKIPASS_PERSON_CERTIFICATE_READ_MORE_DESCRIPTION]"
                ),
              }}
              maxSize={MAX_SIZE}
              acceptedFileTypes={[
                "application/pdf",
                "image/jpeg",
                "image/png",
                "image/gif",
              ]}
              showPreview={false}
              errorText={error?.message}
              isUploaded={!!personCertificate?.id}
              fileName={personCertificate?.fileName}
              isUploading={isUploading}
              onFileSelect={handleFileSelect}
              onFileRemove={() => {
                clearErrors("personCertificate")
                setValue("personCertificate", undefined)
              }}
            />
          )}
        />
      </div>
    </>
  )
}

export default PersonCertificateField
